import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { useAppSelector } from "@/redux/store";
import { extractUsername } from "@/utils/extractUsername";
import {
  CheckCircle,
  Filter,
  Inbox,
  Loader2,
  RefreshCw,
  Search,
  Send,
  UserPlus,
  X,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import {
  cancelFriendRequest,
  getFriendRequests,
  responseFriend,
} from "../../api/friend.api";
import { UserProfileCard } from "../../components";
import { FriendRequest } from "../../types/friend.type";

// Compact Request Card for Sidebar
interface CompactRequestCardProps {
  request: FriendRequest;
  type: "received" | "sent";
  onSelect: (request: FriendRequest) => void;
  onAccept?: (requestId: number) => void;
  onDecline?: (requestId: number) => void;
  onCancel?: (requestId: number) => void;
  isSelected?: boolean;
  isLoading?: boolean;
}

const CompactRequestCard: React.FC<CompactRequestCardProps> = ({
  request,
  type,
  onSelect,
  onAccept,
  onDecline,
  onCancel,
  isSelected = false,
  isLoading = false,
}) => {
  // Determine which user to display (sender or recipient based on type)
  const displayUser = type === "received" ? request.sender : request.recipient;
  const displayName =
    displayUser.name || extractUsername(displayUser.email || "");

  return (
    <div
      className={`group relative p-3 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-md ${
        isSelected
          ? "bg-blue-50 border-blue-200 shadow-sm"
          : "bg-white border-gray-100 hover:border-gray-200"
      }`}
      onClick={() => onSelect(request)}
    >
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0">
          <Avatar className="w-12 h-12">
            <AvatarImage
              src={displayUser.photo || undefined}
              alt={displayName}
              className="object-cover"
            />
            <AvatarFallback className="text-sm font-semibold bg-gradient-to-br from-blue-100 to-purple-100 text-gray-700">
              {displayName.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </div>

        <div className="flex-1 min-w-0">
          <h4
            className={`font-semibold line-clamp-1 ${
              isSelected ? "text-blue-700" : "text-gray-900"
            }`}
          >
            {displayName}
          </h4>
          {displayUser.username && (
            <p className="text-xs text-gray-500 line-clamp-1">
              @{extractUsername(displayUser.username)}
            </p>
          )}
          <div className="flex items-center gap-1 mt-1">
            {type === "received" ? (
              <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">
                <Inbox className="w-2 h-2 mr-1" />
                Đã nhận
              </Badge>
            ) : (
              <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs">
                <Send className="w-2 h-2 mr-1" />
                Đã gửi
              </Badge>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {type === "received" && onAccept && onDecline && (
            <>
              <Button
                size="sm"
                variant="ghost"
                className="w-8 h-8 p-0 text-green-600 hover:bg-green-100"
                onClick={(e) => {
                  e.stopPropagation();
                  onAccept(request.id);
                }}
                disabled={isLoading}
              >
                <CheckCircle className="w-3 h-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="w-8 h-8 p-0 text-red-500 hover:bg-red-100"
                onClick={(e) => {
                  e.stopPropagation();
                  onDecline(request.id);
                }}
                disabled={isLoading}
              >
                <X className="w-3 h-3" />
              </Button>
            </>
          )}
          {type === "sent" && onCancel && (
            <Button
              size="sm"
              variant="ghost"
              className="w-8 h-8 p-0 text-gray-400 hover:bg-gray-100"
              onClick={(e) => {
                e.stopPropagation();
                onCancel(request.id);
              }}
              disabled={isLoading}
            >
              <X className="w-3 h-3" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const FriendRequestsScreen: React.FC = () => {
  const { toast } = useToast();
  const { user } = useAppSelector((state) => state.auth);

  const [receivedRequests, setReceivedRequests] = useState<FriendRequest[]>([]);
  const [sentRequests, setSentRequests] = useState<FriendRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingRequestId, setLoadingRequestId] = useState<number | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<FriendRequest | null>(
    null
  );
  const [activeTab, setActiveTab] = useState<"received" | "sent">("received");

  const loadRequests = async () => {
    try {
      setIsLoading(true);
      const response = await getFriendRequests();

      // Phân loại requests theo current user
      const allRequests = response.data || [];
      const currentUserId = user?.id;

      if (!currentUserId) {
        console.error("Current user ID not found");
        return;
      }

      const received = allRequests.filter(
        (request: FriendRequest) => request.recipient.id === currentUserId
      );

      const sent = allRequests.filter(
        (request: FriendRequest) => request.sender.id === currentUserId
      );

      setReceivedRequests(received);
      setSentRequests(sent);

      // Auto-select first request if none selected
      const currentRequests = activeTab === "received" ? received : sent;
      if (currentRequests.length > 0 && !selectedRequest) {
        setSelectedRequest(currentRequests[0]);
      }
    } catch (error: any) {
      console.error("Error loading friend requests:", error);
      toast({
        title: "Thất bại!",
        variant: "error",
        description: "Không thể tải lời mời kết bạn. Vui lòng thử lại.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  useEffect(() => {
    // Clear selection when switching tabs
    setSelectedRequest(null);
    setSearchQuery("");

    // Auto-select first request in new tab
    const currentRequests =
      activeTab === "received" ? receivedRequests : sentRequests;
    if (currentRequests.length > 0) {
      setSelectedRequest(currentRequests[0]);
    }
  }, [activeTab, receivedRequests, sentRequests]);

  const handleAcceptRequest = useCallback(
    async (requestId: number) => {
      try {
        setLoadingRequestId(requestId);
        await responseFriend(requestId, true);

        // Remove from received requests
        setReceivedRequests((prev) =>
          prev.filter((req) => req.id !== requestId)
        );

        // Clear selected if it was the accepted request
        if (selectedRequest?.id === requestId) {
          setSelectedRequest(null);
        }

        toast({
          title: "Thành công!",
          description: "Đã chấp nhận lời mời kết bạn.",
        });
      } catch (error: any) {
        console.error("Error accepting friend request:", error);
        toast({
          title: "Thất bại!",
          variant: "error",
          description: "Không thể chấp nhận lời mời. Vui lòng thử lại.",
        });
      } finally {
        setLoadingRequestId(null);
      }
    },
    [selectedRequest?.id, toast]
  );

  const handleDeclineRequest = useCallback(
    async (requestId: number) => {
      try {
        setLoadingRequestId(requestId);
        await responseFriend(requestId, false);

        // Remove from received requests
        setReceivedRequests((prev) =>
          prev.filter((req) => req.id !== requestId)
        );

        // Clear selected if it was the declined request
        if (selectedRequest?.id === requestId) {
          setSelectedRequest(null);
        }

        toast({
          title: "Thành công!",
          description: "Đã từ chối lời mời kết bạn.",
        });
      } catch (error: any) {
        console.error("Error declining friend request:", error);
        toast({
          title: "Thất bại!",
          variant: "error",
          description: "Không thể từ chối lời mời. Vui lòng thử lại.",
        });
      } finally {
        setLoadingRequestId(null);
      }
    },
    [selectedRequest?.id, toast]
  );

  const handleCancelRequest = useCallback(
    async (requestId: number) => {
      try {
        setLoadingRequestId(requestId);
        await cancelFriendRequest(requestId);

        // Remove from sent requests
        setSentRequests((prev) => prev.filter((req) => req.id !== requestId));

        // Clear selected if it was the cancelled request
        if (selectedRequest?.id === requestId) {
          setSelectedRequest(null);
        }

        toast({
          title: "Thành công!",
          description: "Đã hủy lời mời kết bạn.",
        });
      } catch (error: any) {
        console.error("Error canceling friend request:", error);
        toast({
          title: "Thất bại!",
          variant: "error",
          description: "Không thể hủy lời mời. Vui lòng thử lại.",
        });
      } finally {
        setLoadingRequestId(null);
      }
    },
    [selectedRequest?.id, toast]
  );

  const handleRefresh = useCallback(() => {
    setSelectedRequest(null);
    loadRequests();
  }, []);

  const handleRequestSelect = (request: FriendRequest) => {
    setSelectedRequest(request);
  };

  // Filter requests based on search query
  const filteredReceivedRequests = receivedRequests.filter((request) => {
    const user = request.sender;
    const name = user.name || extractUsername(user.email || "");
    const username = user.username || "";
    const email = user.email || "";

    return (
      name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      email.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const filteredSentRequests = sentRequests.filter((request) => {
    const user = request.recipient;
    const name = user.name || extractUsername(user.email || "");
    const username = user.username || "";
    const email = user.email || "";

    return (
      name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      email.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const currentRequests =
    activeTab === "received" ? filteredReceivedRequests : filteredSentRequests;
  const totalRequests =
    activeTab === "received" ? receivedRequests.length : sentRequests.length;

  // Convert FriendRequest to Friend format for UserProfileCard
  const convertToFriend = (request: FriendRequest) => {
    const displayUser =
      activeTab === "received" ? request.sender : request.recipient;
    return {
      id: displayUser.id,
      name: displayUser.name,
      email: displayUser.email,
      username: displayUser.username,
      photo: displayUser.photo,
      biography: undefined,
      isOnline: false,
    };
  };

  const handleMessage = (userId: number) => {
    console.log("Navigate to message with user:", userId);
    // TODO: Implement navigation to messages
  };

  const handleCall = (userId: number) => {
    console.log("Start call with user:", userId);
    // TODO: Implement call functionality
  };

  const handleVideoCall = (userId: number) => {
    console.log("Start video call with user:", userId);
    // TODO: Implement video call functionality
  };

  const handleUnfriend = async () => {
    // For requests, this would be accept/decline based on type
    if (activeTab === "received") {
      const request = selectedRequest;
      if (request) {
        await handleAcceptRequest(request.id);
      }
    }
  };

  const handleAddFriend = async () => {
    // For received requests, this means accept
    if (activeTab === "received") {
      const request = selectedRequest;
      if (request) {
        await handleAcceptRequest(request.id);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
          <div className="w-16 h-16 mx-auto mb-4 relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full animate-spin">
              <RefreshCw className="w-8 h-8 text-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
            </div>
          </div>
          <p className="text-gray-600 font-medium">
            Đang tải lời mời kết bạn...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen pt-[70px] gap-4">
      {/* Left Sidebar - Requests List */}
      <div className="w-80 bg-white flex flex-col border-r border-l border-gray-50">
        {/* Header */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-gray-900">Lời mời kết bạn</h1>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleRefresh}
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          {/* Tab Selection */}
          <div className="flex bg-gray-100 p-1 rounded-lg mb-4">
            <button
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                activeTab === "received"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
              onClick={() => setActiveTab("received")}
            >
              <Inbox className="w-4 h-4" />
              Đã nhận
              {receivedRequests.length > 0 && (
                <Badge className="bg-green-500 text-white text-xs">
                  {receivedRequests.length}
                </Badge>
              )}
            </button>
            <button
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                activeTab === "sent"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
              onClick={() => setActiveTab("sent")}
            >
              <Send className="w-4 h-4" />
              Đã gửi
              {sentRequests.length > 0 && (
                <Badge className="bg-blue-500 text-white text-xs">
                  {sentRequests.length}
                </Badge>
              )}
            </button>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Tìm kiếm theo tên, email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-9 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          {/* Stats */}
          {totalRequests > 0 && (
            <div className="flex items-center gap-2 mb-2">
              <Badge
                className={`${
                  activeTab === "received"
                    ? "bg-green-100 text-green-700 border-green-200"
                    : "bg-blue-100 text-blue-700 border-blue-200"
                } text-xs`}
              >
                {activeTab === "received" ? (
                  <Inbox className="w-3 h-3 mr-1" />
                ) : (
                  <Send className="w-3 h-3 mr-1" />
                )}
                {currentRequests.length} / {totalRequests}
              </Badge>
              {searchQuery && (
                <Badge
                  variant="outline"
                  className="border-gray-200 text-gray-600 text-xs"
                >
                  <Filter className="w-3 h-3 mr-1" />"{searchQuery}"
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Requests List */}
        <div className="flex-1 overflow-hidden">
          <div
            id="requests-scrollable"
            className="h-full overflow-y-auto px-4 pb-4"
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                <span className="text-sm text-gray-500">Đang tải...</span>
              </div>
            ) : currentRequests.length === 0 ? (
              <div className="text-center py-8">
                {activeTab === "received" ? (
                  <Inbox className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                ) : (
                  <Send className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                )}
                <h3 className="font-medium text-gray-900 mb-2">
                  {searchQuery
                    ? "Không tìm thấy"
                    : activeTab === "received"
                    ? "Không có lời mời nào"
                    : "Chưa gửi lời mời nào"}
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  {searchQuery
                    ? `Không tìm thấy lời mời nào với "${searchQuery}"`
                    : activeTab === "received"
                    ? "Bạn chưa có lời mời kết bạn nào mới"
                    : "Bạn chưa gửi lời mời kết bạn nào"}
                </p>
                {searchQuery ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSearchQuery("")}
                  >
                    Xóa bộ lọc
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      (window.location.href = "/friends/suggestions")
                    }
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Tìm bạn kết nối
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {currentRequests.map((request) => (
                  <CompactRequestCard
                    key={request.id}
                    request={request}
                    type={activeTab}
                    onSelect={handleRequestSelect}
                    onAccept={
                      activeTab === "received" ? handleAcceptRequest : undefined
                    }
                    onDecline={
                      activeTab === "received"
                        ? handleDeclineRequest
                        : undefined
                    }
                    onCancel={
                      activeTab === "sent" ? handleCancelRequest : undefined
                    }
                    isSelected={selectedRequest?.id === request.id}
                    isLoading={loadingRequestId === request.id}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Content - User Profile */}
      <div className="flex-1 flex flex-col">
        {selectedRequest ? (
          <div className="p-6 overflow-y-auto">
            <UserProfileCard
              user={convertToFriend(selectedRequest)}
              onMessage={handleMessage}
              onCall={handleCall}
              onVideoCall={handleVideoCall}
              onUnfriend={handleUnfriend}
              onAddFriend={handleAddFriend}
              onAcceptRequest={handleAcceptRequest}
              onDeclineRequest={handleDeclineRequest}
              onCancelRequest={handleCancelRequest}
              type={activeTab}
            />
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                {activeTab === "received" ? (
                  <Inbox className="w-8 h-8 text-blue-600" />
                ) : (
                  <Send className="w-8 h-8 text-blue-600" />
                )}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {activeTab === "received"
                  ? "Chọn lời mời để xem"
                  : "Chọn lời mời đã gửi"}
              </h3>
              <p className="text-gray-500 max-w-sm">
                {activeTab === "received"
                  ? "Chọn một lời mời từ danh sách bên trái để xem thông tin chi tiết và phản hồi."
                  : "Chọn một lời mời từ danh sách bên trái để xem thông tin chi tiết."}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FriendRequestsScreen;
